import React from 'react';
import { Box, MenuItem, Paper, TextField, Typography } from '@mui/material';
import useTranslations from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { useEisRuleConfigContext } from '../context';

const WINDOW_DAYS_OPTIONS = [15, 30, 45, 60, 75, 90, 120, 150, 180] as const;

const INCIDENT_COUNT_MIN = 1;
const INCIDENT_COUNT_MAX = 20;

const FrequencySection: React.FC = () => {
  const t = useTranslations();
  const { incidentCount, setIncidentCount, frequencyWindowDays, setFrequencyWindowDays, configuration } =
    useEisRuleConfigContext();

  const templateName = configuration?.scoring?.templates?.[0]?.templateName ?? '';

  return (
    <Paper
      variant='outlined'
      sx={{
        borderColor: neutral[200],
        borderRadius: 1.5,
        p: 3,
        mt: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant='bodyMd'>
            {t('performanceReporting.eis.configuration.configureFrequency.triggerWhen')}
          </Typography>

          {templateName && (
            <Typography
              variant='bodyMd'
              fontWeight={600}
            >
              &ldquo;{templateName}&rdquo;
            </Typography>
          )}

          <Typography variant='bodyMd'>
            {t('performanceReporting.eis.configuration.configureFrequency.reaches')}
          </Typography>

          <TextField
            type='number'
            value={incidentCount ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setIncidentCount(null);
                return;
              }
              const parsed = parseInt(raw, 10);
              if (!isNaN(parsed)) {
                setIncidentCount(Math.min(INCIDENT_COUNT_MAX, Math.max(INCIDENT_COUNT_MIN, parsed)));
              }
            }}
            size='medium'
            inputProps={{ min: INCIDENT_COUNT_MIN, max: INCIDENT_COUNT_MAX }}
            sx={{ width: 100, pt: 0 }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='bodyMd'>
            {t('performanceReporting.eis.configuration.configureFrequency.incidentsWithinTheLast')}
          </Typography>

          <TextField
            select
            value={frequencyWindowDays ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFrequencyWindowDays(val === '' ? null : Number(val));
            }}
            size='small'
            sx={{ width: 90, pt: 0 }}
          >
            {WINDOW_DAYS_OPTIONS.map((n) => (
              <MenuItem
                key={n}
                value={n}
              >
                {n}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant='bodyMd'>
            {t('performanceReporting.eis.configuration.configureFrequency.days')}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default FrequencySection;
